import { Formik, Form, Field } from 'formik';
import useFeatureFlagManagement from './hooks/useFeatureFlagManagement';
import { useNavigate } from 'react-router';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/src/shadComponents/ui/card';
import { Loader2 } from 'lucide-react';
import PageShell from '@/src/Common/components/PageShell';

interface CreateFeatureFlagFormValues {
    key: string;
    enabled: boolean;
    enabledForDevelopment: boolean;
    isPublic: boolean;
}

export default function CreateFeatureFlag() {
    const initialValues: CreateFeatureFlagFormValues = {
        key: '',
        enabled: false,
        enabledForDevelopment: true,
        isPublic: false,
    };

    const navigate = useNavigate();
    const { createFeatureFlag } = useFeatureFlagManagement();

    return (
        <PageShell
            title="Create Feature Flag"
            breadcrumbs={[
                { label: 'Admin', href: '/admin/features' },
                { label: 'Feature Flags', href: '/admin/features' },
                { label: 'Create' },
            ]}
        >
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    await createFeatureFlag(values);
                    setSubmitting(false);

                    navigate(-1);
                }}
            >
                {({ isSubmitting }) => (
                    <Form
                        className="mt-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-12">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10  pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        Feature Flag Details
                                    </h2>
                                </div>
                                <Card className="md:col-span-2">
                                    <CardContent className="pt-6 space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="key">Name</Label>
                                            <Field
                                                as={Input}
                                                id="key"
                                                name="key"
                                                placeholder="Feature Flag Key"
                                            />
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                            <Field
                                                id="enabled"
                                                name="enabled"
                                                type="checkbox"
                                            />
                                            <Label htmlFor="enabled">Enabled</Label>
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                            <Field
                                                id="enabledForDevelopment"
                                                name="enabledForDevelopment"
                                                type="checkbox"
                                            />
                                            <Label htmlFor="enabledForDevelopment">
                                                Development Enabled
                                            </Label>
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                            <Field
                                                id="isPublic"
                                                name="isPublic"
                                                type="checkbox"
                                            />
                                            <Label htmlFor="isPublic">
                                                Public Facing
                                            </Label>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-4 pt-6">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                                Create
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </PageShell>
    );
}
